import React, { useContext, useEffect, useState } from 'react'
import { Grid, makeStyles, Typography } from '@material-ui/core'
import * as d3 from 'd3'
import { isMobile } from 'react-device-detect'
import PageSection from '../components/PageSection'
import FocusChart from '../components/FocusChart'
import ContextChart from '../components/ContextChart'
import NewsFeed from '../components/NewsFeed'
import TimeRangeSelector from '../components/TimeRangeSelector'
import { monthAdd } from '../ultils/dateUtils'
import {
  MAX,
  ONE_YEAR,
  SIX_MONTHS,
  THREE_MONTHS,
  TWO_YEARS,
} from '../constants/FilterDateConstantValue'
import { Context as PortfolioContext } from '../contexts/portfolio'
import TwoValuesToggle from '../components/TwoValuesToggle'

const useStyles = makeStyles({
  subheading: {
    marginBottom: 10,
    fontSize: 14,
    color: '#474747',
    fontWeight: 600,
  },
  marketContextContainer: {
    borderTop: 'unset',
    background: '#fff',
    margin: 'unset !important',
    width: '100% !important',
    '& > div:first-child': {
      borderRight: '1px solid #DFE4EB',
    },
    '& .MuiGrid-item': {
      padding: '20px 20px',
    },
  },
  timeRange: {
    margin: '0 0px 28px 0px',
  },
  eventFeed: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E8EBEF',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    color: '#474747',
    '& > div': {
      backgroundColor: '#F3F3F3',
      padding: '8px 12px',
      marginRight: 10,
      borderRadius: 20,
    },
  },
  bfaiBtn: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& > div': {
      marginRight: 10,
      backgroundColor: '#3C5D9F',
      width: 16,
      height: 16,
      borderRadius: '50%',
    },
  },
  industryBtn: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& > div': {
      marginRight: 10,
      backgroundColor: '#F8DC4F',
      width: 16,
      height: 16,
      borderRadius: '50%',
    },
  },
  chartContainer: {
    paddingLeft: '0px !important',
  },
  chartContainerMobile: {
    padding: '0px !important',
    border: 'none !important',
  },
  newsListContainer: {
    paddingRight: '0px !important',
    position: 'relative',
  },
  newsListContainerMobile: {
    padding: '0px !important',
  },
  blurBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 50,
    width: '100%',
    backgroundImage:
      'linear-gradient(180deg, rgba(255, 255, 255, 0) -10.75%, #FFFFFF 70.43%)',
  },
})

const MarketContext = ({ tickerId, riskStatus }) => {
  const classes = useStyles()
  const { state, getPrices, getEvents } = useContext(PortfolioContext)
  const [transform, setTransform] = useState(null) // for zoom
  const [selection, setSelection] = useState(null) // for brush
  const [selectedEvents, setSelectedEvents] = useState([])
  const [selectedTime, setSelectedTime] = useState({
    title: isMobile ? ONE_YEAR : TWO_YEARS,
  })
  const [contextChart, setContextChart] = useState(null)
  const [focusChart, setFocusChart] = useState(null)
  const [eventData, setEventData] = useState([])
  const [priceData, setPriceData] = useState([])
  const [marketContextToggleObj, setMarketContextToggleObj] = useState({
    showUnrecognisedRiskStatus: true,
    showRiskEvents: false,
  })

  useEffect(() => {
    if (
      !!contextChart &&
      !!focusChart &&
      eventData.length > 0 &&
      contextChart.data.length > 0
    ) {
      focusChart.toggleUpdate({ toggleProp: marketContextToggleObj })
    }
  }, [marketContextToggleObj, focusChart, contextChart])

  useEffect(() => {
    if (
      !!contextChart &&
      !!focusChart &&
      eventData.length > 0 &&
      contextChart.data.length > 0
    ) {
      const lastDate = d3.max(contextChart.data, d => d.date)
      const minDate = d3.min(contextChart.data, d => d.date)
      lastDate.setDate(lastDate.getDate() + 1)
      let cutoffDate = new Date()
      switch (selectedTime?.title) {
        case THREE_MONTHS:
          cutoffDate = monthAdd(lastDate, -3)
          break
        case SIX_MONTHS:
          cutoffDate = monthAdd(lastDate, -6)
          break
        case ONE_YEAR:
          cutoffDate = monthAdd(lastDate, -12)
          break
        case TWO_YEARS:
          cutoffDate = monthAdd(lastDate, -24)
          break
        case MAX:
          cutoffDate = minDate
          break
        default:
          cutoffDate = minDate
          break
      }

      const fromDate = d3.max([minDate, cutoffDate])
      const selection = [fromDate, lastDate].map(d => contextChart.xTime(d))

      contextChart.brush.move(contextChart.g.select('.brush'), selection)
      focusChart.brushed({ selection, sourceEvent: {} })
    }
  }, [selectedTime, contextChart, focusChart, eventData])

  useEffect(() => {
    setEventData(state.marketContext.eventData)
    setSelectedEvents(state.marketContext.eventData)
    setPriceData(state.marketContext.priceData)
  }, [state.marketContext])

  const registerContextChartTimeChange = vis => {
    setContextChart(vis)
  }
  const registerFocusChartTimeChange = vis => {
    setFocusChart(vis)
  }

  return (
    <PageSection
      title="Risk Events"
      subTitle="Identification of Risk Patterns"
      customStyle={{ height: isMobile ? 'unset' : '570px' }}
      riskEventToolTip
      isShowingRisk={false}
      riskStatus={riskStatus}
    >
      <></>
      <Grid className={classes.marketContextContainer} container spacing={3}>
        <Grid
          className={
            isMobile ? classes.chartContainerMobile : classes.chartContainer
          }
          item
          xs={12}
          sm={7}
        >
          <div
            className={classes.timeRange}
            style={{ marginTop: isMobile ? '16px' : 'unset' }}
          >
            <TimeRangeSelector
              data={[
                { title: MAX },
                { title: TWO_YEARS },
                { title: ONE_YEAR },
                { title: SIX_MONTHS },
                { title: THREE_MONTHS },
              ]}
              setItemActiveIndex={item => setSelectedTime(item)}
              defaultValue={isMobile ? ONE_YEAR : TWO_YEARS}
            />
          </div>
          <div style={{ height: '100px', marginBottom: '10px' }}>
            <ContextChart
              data={priceData}
              events={eventData}
              registerTimeChange={registerContextChartTimeChange}
              selection={selection}
              setSelection={setSelection}
              transform={transform}
              setSelectedEvents={setSelectedEvents}
            />
          </div>
          <div style={{ height: '30px', marginBottom: '40px' }}>
            <TwoValuesToggle
              setToggleObj={val => setMarketContextToggleObj(val)}
              toggleObj={marketContextToggleObj}
              textOption1="Equity Risk Status"
              textOption2="Show Risk Events"
            />
          </div>
          <div style={{ height: '200px', marginBottom: '0px' }}>
            <FocusChart
              data={priceData}
              events={eventData}
              toggleProps={marketContextToggleObj}
              registerFocusChartTimeChange={registerFocusChartTimeChange}
              selection={selection}
              transform={transform}
              setTransform={setTransform}
              currency={state?.marketContext?.currency}
            />
          </div>
        </Grid>

        <Grid
          className={
            isMobile
              ? classes.newsListContainerMobile
              : classes.newsListContainer
          }
          item
          xs={12}
          sm={5}
        >
          <div className={classes.eventFeed}>
            <Typography className={classes.subheading} variant="subtitle1">
              Risk Story
            </Typography>
          </div>
          <NewsFeed events={selectedEvents} />
        </Grid>
      </Grid>
    </PageSection>
  )
}

export default MarketContext
